/* @flow */
import { API_URL } from '../../Constants';
import type { TripContact, TripRequest, Review } from '../../data/TripRequests';

export type CreateTripRequestOptions = {
  contacts?: TripContact[],
  deliveryFrom?: string,
  deliveryTo?: string,
  description?: string,
  destinationId?: string,
  originId?: string,
  fareEstimateId?: string,
  label?: string,
  paymentSourceId?: string,
  product?: string,
  promoCode?: string,
  sessionId?: string,
  useCase?: string,
};

export function createTripRequest(options: CreateTripRequestOptions) {
  return {
    url: `${API_URL}/trip-requests`,
    body: {
      contacts: options.contacts,
      delivery_from: options.deliveryFrom,
      delivery_to: options.deliveryTo,
      description: options.description,
      destination_id: options.destinationId,
      fare_estimate_id: options.fareEstimateId,
      origin_id: options.originId,
      payment_source_id: options.paymentSourceId,
      product: options.product,
      query_string: options.queryString,
      use_case: options.useCase,
      session_id: options.sessionId,
      label: options.label,
      promo_code: options.promoCode,
      photo_urls: options.photoUrls,
    },
    options: {
      method: 'POST',
    },
    transform: (response: Object) => ({ tripRequests: response }),
    update: {
      tripRequests: (prevTripRequests: Object, newTripRequest: Object) => ({
        ...prevTripRequests,
        [newTripRequest.id]: newTripRequest,
      }),
    },
  };
}

export function updateTripRequest(tripRequestId, changes: CreateTripRequestOptions) {
  return {
    url: `${API_URL}/trip-requests/${tripRequestId}`,
    body: {
      contacts: changes.contacts,
      delivery_from: changes.deliveryFrom,
      delivery_to: changes.deliveryTo,
      label: changes.label,
      description: changes.description,
      destination_id: changes.destinationId,
      origin_id: changes.originId,
      product: changes.product,
      fare_estimate_id: changes.fareEstimateId,
      payment_source_id: changes.paymentSourceId,
      promo_code: changes.promoCode,
    },
    options: {
      method: 'PATCH',
    },
    transform: (response: Object) => ({ tripRequests: response }),
    update: {
      tripRequests: (prevTripRequests: Object, newTripRequest: Object) => ({
        ...prevTripRequests,
        [newTripRequest.id]: newTripRequest,
      }),
    },
  };
}

export function getActiveTripRequests() {
  return {
    url: `${API_URL}/trip-requests`,
    options: {
      method: 'GET',
    },
    body: {
      filter_active: true,
    },
    transform: (response: TripRequest[]) => ({
      tripRequests: response.reduce((byId, tripRequest) => {
        byId[tripRequest.id] = tripRequest;
        return byId;
      }, {}),
    }),
    update: {
      tripRequests: (
        prevTripRequests: { [id: string]: TripRequest },
        newTripRequests: { [id: string]: TripRequest }
      ) => ({
        ...prevTripRequests,
        ...newTripRequests,
      }),
    },
  };
}

export function getTripRequestById(tripRequestId: string) {
  return {
    url: `${API_URL}/trip-requests/${tripRequestId}`,
    options: {
      method: 'GET',
    },
    transform: (response: Object) => ({ tripRequests: response }),
    update: {
      tripRequests: (prevTripRequests: Object, newTripRequest: Object) => ({
        ...prevTripRequests,
        [newTripRequest.id]: newTripRequest,
      }),
    },
  };
}

export function cancelTripRequest(tripRequestId: string, reason: string) {
  return {
    url: `${API_URL}/trip-requests/${tripRequestId}/actions/cancel`,
    body: {
      cancel_reason: reason,
    },
    options: {
      method: 'PUT',
    },
    transform: (response: Object) => ({ tripRequests: response }),
    update: {
      tripRequests: (prevTripRequests: Object, newTripRequest: Object) => ({
        ...prevTripRequests,
        [newTripRequest.id]: newTripRequest,
      }),
    },
  };
}

export function reviewTripRequest(tripRequestId: string, review: Review) {
  // there is a bug that i'm unable to consistenly reproduce where
  // review.rating is sometimes being passed as a string and then
  // the api errors because it needs to be a number. So for the time
  // being lets just force the number.
  review.rating = parseInt(review.rating, 10);

  return {
    url: `${API_URL}/trip-requests/${tripRequestId}/actions/review`,
    options: {
      method: 'PUT',
    },
    body: review,
    transform: (response: Object) => ({ tripRequests: response }),
    update: {
      tripRequests: (prevTripRequests: Object, newTripRequest: Object) => ({
        ...prevTripRequests,
        [newTripRequest.id]: newTripRequest,
      }),
    },
  };
}



// WEBPACK FOOTER //
// ./src/redux/trip-requests/queries.js